import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Typography} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import useIndustry from "../../../../../hooks/use-industry";
import useSelectedCategory from "../../../../../hooks/use-selected-category";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";
import Api from "../../../../../../core/services/api/api";
import DateUtils from "../../../../../../core/services/date-utils";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import {PriceMatricesDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import Button from "../../../../../components/base/button/button";
import {PriceMatrixSources, PriceMatrixTypes} from "../../../../../components/dialogs/upsert-price-matrix";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";

const dataGridSaveConfig = {
    name: "ProductApplicablePriceMatricesDataGrid",
    version: 1,
};

const PAGE_SIZE = 5;

/**
 * Read-only datagrid of active system price matrices applicable to a product
 * (part match, root category match, or general/system-wide), with customer null.
 *
 * @param {Object} props
 * @param {number} props.partId
 * @param {number} [props.categoryId]
 * @param {number} [props.industryId]
 */
const ProductApplicablePriceMatrices = ({partId, categoryId, industryId}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.product-applicable-price-matrices'});
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(true);
    const [matrices, setMatrices] = useState([]);
    const [currentPage, setCurrentPage] = useState(DefaultPaginationInfo.currentPage);
    const {categoriesOfFetchedIndustry: categories, fetchIndustry} = useIndustry();
    const {selectedCategoriesHierarchy} = useSelectedCategory(categories, categoryId);
    const {priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});

    const rootCategoryId = selectedCategoriesHierarchy?.[0]?.id;

    /**
     * Loads industry categories so the root category can be resolved from the leaf.
     */
    useEffect(() => {
        if (industryId) {
            void fetchIndustry({industryId});
        }
    }, [fetchIndustry, industryId]);

    /**
     * Fetches system price matrices.
     */
    const getPriceMatrices = useCallback(async () => {
        setLoading(true);
        const response = await Api.getSystemPriceMatrices();
        if (!isMounted()) {
            return;
        }
        setMatrices(response?.data ?? []);
        setCurrentPage(1);
        setLoading(false);
    }, [isMounted]);

    useEffect(() => {
        if (!partId) {
            return;
        }
        void getPriceMatrices();
    }, [getPriceMatrices, partId]);

    /**
     * Whether a matrix is currently active by start/end dates.
     * @param {object} matrix
     */
    const isMatrixActive = useCallback((matrix) => (
        (!matrix.endDate || DateUtils.serverToLocal(matrix.endDate).isSameOrAfter(DateUtils.localNow(), 'day')) &&
        (!matrix.startDate || DateUtils.serverToLocal(matrix.startDate).isSameOrBefore(DateUtils.localNow(), 'day'))
    ), []);

    /**
     * Whether a matrix applies to this product (part, root category, or general system).
     * @param {object} matrix
     */
    const isMatrixApplicable = useCallback((matrix) => {
        if (matrix.customer) {
            return false;
        }
        if (matrix.part?.id === partId) {
            return true;
        }
        if (rootCategoryId && matrix.partCategory?.id === rootCategoryId) {
            return true;
        }
        return !matrix.part && !matrix.partCategory && !matrix.priceLevel;
    }, [partId, rootCategoryId]);

    const transformedMatrices = useMemo(() => {
        return matrices
            .filter((matrix) => isMatrixActive(matrix) && isMatrixApplicable(matrix))
            .map((matrix) => {
                const type = matrix.part !== null
                    ? PriceMatrixTypes.part
                    : matrix.partCategory !== null
                        ? PriceMatrixTypes.category
                        : matrix.priceLevel !== null
                            ? PriceMatrixTypes.priceLevel
                            : PriceMatrixTypes.general;
                let target;
                switch (type.id) {
                    case PriceMatrixTypes.part.id:
                        target = matrix.part?.partNo;
                        break;
                    case PriceMatrixTypes.category.id:
                        target = matrix.partCategory?.title;
                        break;
                    case PriceMatrixTypes.priceLevel.id:
                        target = priceLevelDict?.find((p) => p.priceLevelId === matrix.priceLevel?.id)?.title ?? matrix.priceLevel?.title;
                        break;
                    case PriceMatrixTypes.general.id:
                    default:
                        target = PriceMatrixTypes.general.title;
                        break;
                }
                let change = matrix.isPercentage ? `${matrix.value}%` : formatMoney(matrix.value);
                if (matrix.isFixedValue) {
                    change = change.concat(' (Fixed)');
                }
                const basedOn = matrix.isBasedOnCost ? PriceMatrixSources.cost.title : PriceMatrixSources.finalPrice.title;
                return {change, basedOn, type, target, matrix};
            });
    }, [isMatrixActive, isMatrixApplicable, matrices, priceLevelDict]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: t('columns.type'),
                name: PriceMatricesDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: t('columns.target'),
                name: PriceMatricesDataGridCellKeys.target,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: t('columns.change'),
                name: PriceMatricesDataGridCellKeys.change,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: t('columns.basedOn'),
                name: PriceMatricesDataGridCellKeys.basedOn,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: t('columns.startDate'),
                name: PriceMatricesDataGridCellKeys.startDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
            },
            {
                title: t('columns.endDate'),
                name: PriceMatricesDataGridCellKeys.endDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
            },
        ], [t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const visibleMatrices = useMemo(() => {
        const start = Math.min(Math.max((currentPage - 1) * PAGE_SIZE, 0), transformedMatrices.length);
        const end = Math.min(currentPage * PAGE_SIZE, transformedMatrices.length);
        return transformedMatrices.slice(start, end);
    }, [currentPage, transformedMatrices]);

    const paginationInfo = useMemo(() => ({
        ...DefaultPaginationInfo,
        pageSize: PAGE_SIZE,
        sizes: [PAGE_SIZE],
        currentPage: Math.min(currentPage, Math.ceil(transformedMatrices.length / PAGE_SIZE) || 1),
        length: transformedMatrices.length,
    }), [currentPage, transformedMatrices.length]);

    /** @type {DataGridRow[]} */
    const dataGridRows = useMemo(() => {
        return visibleMatrices.map(({type, target, change, basedOn, matrix}) => ({
            key: matrix.id,
            data: matrix,
            cells: {
                [PriceMatricesDataGridCellKeys.type]: type?.title,
                [PriceMatricesDataGridCellKeys.target]:
                    type?.id === PriceMatrixTypes.part.id
                        ? <ProductInformationPopover
                            partNo={target}
                            wrapperProps={{
                                ...dataGridCellTextProps,
                            }}
                        >
                            {target ?? ""}
                        </ProductInformationPopover>
                        : target,
                [PriceMatricesDataGridCellKeys.change]: change,
                [PriceMatricesDataGridCellKeys.basedOn]: basedOn,
                [PriceMatricesDataGridCellKeys.startDate]: matrix.startDate,
                [PriceMatricesDataGridCellKeys.endDate]: matrix.endDate,
            },
        }));
    }, [visibleMatrices]);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, width: '100%'}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t('title')}
                </Typography>
                <Button
                    component={Link}
                    to={routes.settings.priceMatrix.list}
                    variant={'outlined'}
                    color={'primary'}
                    endIcon={<OpenInNew className={'icon small'}/>}
                >
                    {t('manageButton')}
                </Button>
            </Box>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={dataGridRows}
                hideExporting
                hideFooterActions
                state={{
                    pagination: paginationInfo,
                    loading: {state: loading},
                    density: dataGridDensity,
                }}
                onCurrentPageChanged={(_, current) => setCurrentPage(current.currentPage)}
            />
        </Box>
    );
};

export default ProductApplicablePriceMatrices;
